import { useTranslation } from "gatsby-plugin-react-i18next";
import { uniq } from "ramda";
import { useEffect, useState } from "react";

import { carService } from "../../services/car";
import { useFlowNavigation } from "../useFlowNavigation";

import { PageNames, VehicleType } from "@enums";
import { MakeDto, ModelDto, SelectBoxInterface } from "@types";

export function useCarService(make: string, model: string) {
    const isSSR = typeof window === "undefined";
    const { navigateTo } = useFlowNavigation();
    const { t } = useTranslation();

    const [carMakesAndModels, setCarMakesAndModels] = useState<
        Array<MakeDto> | undefined
    >(undefined);

    const [carModelDetails, setCarModelDetails] = useState<
        ModelDto | undefined
    >(undefined);

    useEffect(() => {
        if (!isSSR) {
            carService
                .getAllMakes()
                .then((r) => {
                    setCarMakesAndModels(r);
                })
                .catch((error) => {
                    console.error(error);
                    navigateTo(PageNames.TechnicalDifficulties);
                });
        }
        // eslint-disable-next-line
    }, [isSSR]);

    useEffect(() => {
        if (make !== "" && model !== "") getCarModelDetails(make, model);
        if (model === "") setCarModelDetails(undefined);
        // eslint-disable-next-line
    }, [make, model]);

    const getCarMakes = () => {
        if (carMakesAndModels && carMakesAndModels.length > 0) {
            const mappedMakes = carMakesAndModels.map((car) => {
                return { value: car.name, label: car.name };
            }) as Array<SelectBoxInterface>;
            return mappedMakes;
        }
        return undefined;
    };

    const carMakes = getCarMakes();

    const getCarModelsForMake = (make: string) => {
        if (
            make &&
            make !== "" &&
            carMakesAndModels &&
            carMakesAndModels.length > 0
        ) {
            let mappedModels;
            carMakesAndModels.forEach((car) => {
                if (car.name === make && car.models && car.models.length > 0) {
                    mappedModels = car.models.map((model) => {
                        return { value: model, label: model };
                    }) as Array<SelectBoxInterface>;
                }
            });
            return mappedModels;
        }
        return undefined;
    };

    const getCarModelDetails = (make: string, model: string) => {
        carService
            .getDetailsOfModel(make, model)
            .then((r) => {
                if (r) setCarModelDetails(r);
            })
            .catch((error) => {
                console.error(error);
                navigateTo(PageNames.TechnicalDifficulties);
            });
    };

    const getCarFuelForModel = () => {
        if (carModelDetails) {
            const newFuelTypes = [] as string[];
            carModelDetails.possibleFuelTypes?.forEach((fuel) => {
                if (fuel.fuelType) newFuelTypes.push(fuel.fuelType);
            });
            const mappedFuelTypes = newFuelTypes.map((fueltype) => {
                return {
                    value: fueltype,
                    label: t(
                        `cardescription.fuel.${fueltype
                            .toString()
                            .toLowerCase()}`,
                    ),
                };
            });
            return mappedFuelTypes;
        }
        return undefined;
    };

    const getCarPowerForFuel = (fuel: string) => {
        if (carModelDetails && fuel !== "") {
            const fuelObj = carModelDetails?.possibleFuelTypes?.find(
                (possibleFuel) => {
                    return possibleFuel.fuelType === fuel;
                },
            );
            if (fuelObj) {
                const uniqueKws = fuelObj.possibleKws
                    ? uniq(fuelObj.possibleKws)
                    : [];

                return uniqueKws.map((power) => {
                    return {
                        value: String(power),
                        label: `${String(power)} ${t(
                            "cardescription.power.abbr",
                        )} (${Math.round(Number(power) * 1.362)} ${t(
                            "cardescription.power.horsepower",
                        )})`,
                    };
                });
            } else {
                return undefined;
            }
        }
        return undefined;
    };

    const carVehicleType = carModelDetails
        ? (carModelDetails.vehicleType as VehicleType)
        : undefined;

    return {
        carMakes,
        carVehicleType,
        getCarModelsForMake,
        getCarFuelForModel,
        getCarPowerForFuel,
    };
}
